import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	output,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
	InsightResponseMeta,
	KristaConfidence,
	KristaNarrativeStatus,
} from '@marmon/shared/types/krista';
import { TuiButton, TuiIcon, TuiPopup } from '@taiga-ui/core';
import { TuiBadge, TuiDrawer, TuiStatus } from '@taiga-ui/kit';

/** Splits plain insight text on blank lines. Single newlines stay in a paragraph. */
export function insightMessageParagraphs(text: string): string[] {
	return text
		.split(/\n{2,}/)
		.map((paragraph) => paragraph.trim())
		.filter((paragraph) => paragraph.length > 0);
}

/** Local clock for a Krista `generatedAt` instant. Invalid values pass through. */
export function insightInstantLabel(
	value: string | null | undefined,
	timeZone?: string,
): string {
	if (!value) return '';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return new Intl.DateTimeFormat('en', {
		dateStyle: 'medium',
		timeStyle: 'short',
		...(timeZone ? { timeZone } : {}),
	}).format(date);
}

/**
 * Side panel for a finished AI narrative. The caller owns the open state
 * and supplies the header, which insight this is, and the shared response
 * metadata. The narrative sits under that metadata.
 */
@Component({
	standalone: true,
	selector: 'mar-insight-panel',
	imports: [
		TranslocoPipe,
		TuiButton,
		TuiIcon,
		TuiPopup,
		TuiBadge,
		TuiDrawer,
		TuiStatus,
	],
	templateUrl: 'insight-panel.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { class: 'contents' },
})
export class InsightPanelComponent {
	readonly open = input(false);
	readonly title = input.required<string>();
	readonly description = input('');
	/** Which insight this panel is showing, in the caller's words. */
	readonly context = input('');
	readonly meta = input<InsightResponseMeta | null>(null);
	readonly message = input('');
	readonly closed = output<void>();

	readonly paragraphs = computed(() =>
		insightMessageParagraphs(this.message()),
	);

	readonly generatedLabel = computed(() =>
		insightInstantLabel(this.meta()?.generatedAt),
	);

	readonly statusAppearance = computed(() =>
		insightStatusAppearance(this.meta()?.status),
	);

	readonly confidenceAppearance = computed(() =>
		insightConfidenceAppearance(this.meta()?.confidence),
	);

	readonly showMeta = computed(() => !!this.context() || this.meta() != null);

	onClose() {
		this.closed.emit();
	}
}

function insightStatusAppearance(
	status: KristaNarrativeStatus | undefined,
): string {
	switch (status) {
		case 'generated':
			return 'positive';
		case 'generated_with_caveat':
			return 'warning';
		case 'declined':
			return 'negative';
		default:
			return 'neutral';
	}
}

function insightConfidenceAppearance(
	confidence: KristaConfidence | undefined,
): string {
	switch (confidence) {
		case 'high':
			return 'positive';
		case 'medium':
			return 'warning';
		default:
			return 'neutral';
	}
}
